import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { brandIcons } from '../../../config/branding';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAccountRevenueTotal, fetchPayout } from '../../../redux/slices/PayoutSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import getFirstSubdomain from '../../../shared-functions/DomainsplitFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import { integerValidation } from '../../../shared-functions/Validation';
import accountAPI from '../../../utils/api/account';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * Payout Content component
 * @param {any} param0 - The param0
 * @param {any} param0.setOpenStatus - The setOpenStatus prop
 * @param {any} param0.setModalView - The setModalView prop
 */
function PayoutContent({
  setOpenStatus,
  setModalView,
}: {
  setOpenStatus: Dispatch<SetStateAction<boolean>>;
  setModalView: Dispatch<SetStateAction<ModalInterface>>;
}) {
  const { t } = useTranslation('account');
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const { revenueTotal } = useAppSelector((state) => state.payout);

  const accountCustomId = id || searchParameters.get('accountCustomId');
  const [paymentType, setPaymentType] = useState<'full' | 'partial'>('full');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'stripe' | 'online' | 'writeOff'>(
    'cash',
  );
  const [amount, setAmount] = useState('');
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [buttonStatus, setButtonStatus] = useState(false);
  const dispatch = useAppDispatch();
  const subdomain = getFirstSubdomain();

  // hide if any subdomain exists
  const shouldHideAccessUtility = Boolean(subdomain);
  /**
   * Handle approve payment
   */
  const handleApprovePayment = async () => {
    if (amount === '') {
      setShowFormErrors(true);
      return;
    }
    if (!accountCustomId) {
      throw new Error('accountId is required');
    }
    try {
      setButtonStatus(true);
      if (paymentMethod !== 'stripe') {
        await accountAPI.createRevenuePayoutHistory(accountCustomId, {
          payAmount: Number(amount),
          mode: paymentMethod,
          paymentType,
        });
      } else {
        await accountAPI.createRevenuePayoutStripe(accountCustomId, {
          payAmount: Number(amount),
        });
      }
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title=""
            description={t('payoutSuccess')}
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              setButtonStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
      setTimeout(() => {
        dispatch(fetchAccountRevenueTotal({ accountCustomId }));
        dispatch(fetchPayout({ accountCustomId }));
      }, 3000);

      setOpenStatus(true);
    } catch (error: any) {
      setButtonStatus(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (paymentType === 'full') {
      setAmount(ToFixedValue(revenueTotal?.pendingBalance?.toString() ?? ''));
    } else {
      setAmount('');
    }
  }, [paymentType, revenueTotal]);

  return (
    <div className="payout-content-modal-box">
      {/* Payment Type */}
      <div className="radio-group">
        <CustomRadioButton
          name="paymentType"
          value="full"
          label={t('full')}
          selected={paymentType === 'full'}
          onChangeFunc={(name, value) => setPaymentType(value as 'full' | 'partial')}
        />
        <CustomRadioButton
          name="paymentType"
          value="partial"
          label={t('partial')}
          selected={paymentType === 'partial'}
          onChangeFunc={(name, value) => setPaymentType(value as 'full' | 'partial')}
        />
      </div>

      {/* Amount Input */}
      <div className="amount-box">
        <CustomInput
          name="amount"
          inputLabel={t('amount')}
          type="text" // text type to control input
          placeholder={t('enterAmountHere')}
          value={amount}
          onChangeValue={(name, value) => {
            let numericValue = value.toString().replace(/[^0-9.]/g, '');
            const parts = numericValue.split('.');
            if (parts.length > 2) {
              numericValue = `${parts[0]}.${parts[1]}`;
            }
            const integerPart = parts[0] || '0';
            if (integerPart && !integerValidation(1, 10, Number(integerPart))) {
              return;
            }
            setAmount(numericValue);
          }}
          valueRequired
          error={showFormErrors && amount === ''}
          errorMessage={t('amountRequired')}
          frontSvgIcon={<brandIcons.currencyIcon className="icon" />}
          disabled={paymentType === 'full'}
          maxValue={1000}
          decimalLimit={3}
        />
      </div>

      {/* Payment Method */}
      <div className="radio-group method-group">
        <CustomRadioButton
          name="paymentMethod"
          value="cash"
          label={t('cash')}
          selected={paymentMethod === 'cash'}
          onChangeFunc={(name, value) =>
            setPaymentMethod(value as 'cash' | 'stripe' | 'online' | 'writeOff')
          }
        />
        {!shouldHideAccessUtility && (
          <CustomRadioButton
            name="paymentMethod"
            value="stripe"
            label={t('stripe')}
            selected={paymentMethod === 'stripe'}
            onChangeFunc={(name, value) =>
              setPaymentMethod(value as 'cash' | 'stripe' | 'online' | 'writeOff')
            }
          />
        )}

        <CustomRadioButton
          name="paymentMethod"
          value="online"
          label={t('online')}
          selected={paymentMethod === 'online'}
          onChangeFunc={(name, value) =>
            setPaymentMethod(value as 'cash' | 'stripe' | 'online' | 'writeOff')
          }
        />
        <CustomRadioButton
          name="paymentMethod"
          value="writeOff"
          label={t('writeOff')}
          selected={paymentMethod === 'writeOff'}
          onChangeFunc={(name, value) =>
            setPaymentMethod(value as 'cash' | 'stripe' | 'online' | 'writeOff')
          }
        />
      </div>

      {/* Buttons */}
      <div className="button-group">
        <MyButton
          label={t('cancel')}
          buttonType="danger-outline"
          onClickFunc={() => setOpenStatus(false)}
          disableButton={buttonStatus}
        />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={handleApprovePayment}
          loading={buttonStatus}
        />
      </div>
    </div>
  );
}

export default PayoutContent;
